/**
 * Link do manifesto do app (<link rel="manifest">) — quando ele existe (#291)
 *
 * Basta a página ter um manifesto instalável para o navegador oferecer a instalação por
 * conta própria (ícone de instalar na barra do Chrome no computador, "App disponível"
 * do Edge), mesmo com o `beforeinstallprompt` cancelado. Por isso o HTML não traz link
 * de manifesto (o vite.config.ts tira o que o vite-plugin-pwa injeta) e ele só é posto
 * na página onde a instalação é desejada:
 * - painel: manifesto do Vende Aí (`/manifest.webmanifest`, gerado no build), em todas
 *   as rotas do painel (PWAProvider);
 * - loja online: manifesto DA LOJA, só enquanto o convite pós-pedido (ou a janela do
 *   link "Instalar o app da loja") estiver aberto (StoreAppInstall);
 * - site institucional, blog, cadastro/login e mesa: nunca.
 *
 * Cada dono marca o próprio link (`data-manifest-owner`) e só mexe nele.
 *
 * @author Equipe VendeAi
 * @since 2.4.0
 */

import { resetInstallPrompt } from './pwaInstall';

export type ManifestOwner = 'panel' | 'store';

/** Manifesto do app do painel (gerado pelo vite-plugin-pwa) */
export const PANEL_MANIFEST_HREF = '/manifest.webmanifest';

function findLink(owner: ManifestOwner): HTMLLinkElement | null {
  return document.head.querySelector<HTMLLinkElement>(
    `link[rel="manifest"][data-manifest-owner="${owner}"]`,
  );
}

/** Põe (ou atualiza) o link de manifesto deste dono */
export function setManifestLink(owner: ManifestOwner, href: string): void {
  if (typeof document === 'undefined') return;
  let link = findLink(owner);
  if (link && link.getAttribute('href') === href) return;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    link.dataset.manifestOwner = owner;
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
  // Evento de instalação guardado era de outro manifesto: o navegador manda outro
  resetInstallPrompt();
}

/** Tira o link de manifesto deste dono (a página deixa de ser instalável) */
export function removeManifestLink(owner: ManifestOwner): void {
  if (typeof document === 'undefined') return;
  const link = findLink(owner);
  if (!link) return;
  link.remove();
  resetInstallPrompt();
}
